/**
 * PlaceCoreEditorCard — the editorial heart of the place admin.
 * Single unified section: "Platsens karaktär" (max 6 intents).
 * Reads from canonical `intents` table, writes via `place_intents` join.
 * Sync trigger keeps place_core.intents up to date for matching compat.
 */
import { Textarea } from "@/components/ui/textarea";
import { FarmlyRichTextField } from "@/components/content/FarmlyRichTextField";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, ExternalLink, Loader2, Check, X } from "lucide-react";
import { SEASON_CHIP_OPTIONS, DURATION_OPTIONS } from "./intents-options";
import type { PlaceCoreData } from "./placeCoreSchema";
import { MAX_PLACE_INTENTS, MAX_VIBE_LENGTH } from "./placeCoreSchema";
import { useIntents, usePlaceIntents } from "@/hooks/useIntents";
import { usePlaceIntentsMutation } from "@/hooks/usePlaceIntentsMutation";
import { EditorialAiAssistant } from "@/components/ai/EditorialAiAssistant";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";

interface Props {
  data: PlaceCoreData;
  onChange: (data: PlaceCoreData) => void;
  slug?: string;
  placeId?: string;
  placeName?: string;
  municipality?: string;
  landskap?: string;
  tags?: string[];
  disabled?: boolean;
}

/** Deterministic autofill fallback (no AI call). */
function buildAutofill(name: string, municipality?: string, landskap?: string): Partial<PlaceCoreData> {
  const region = [municipality, landskap].filter(Boolean).join(", ");
  const vibe = `${name} är en plats${region ? ` i ${region}` : ""} där tempot sänks och omgivningen talar för sig själv. Här möts besökare av lugn, natur och ett stilla landskap som inbjuder till eftertanke.`;
  return { vibe, seasons: [] };
}

export function PlaceCoreEditorCard({
  data,
  onChange,
  slug,
  placeId,
  placeName,
  municipality,
  landskap,
  tags,
  disabled,
}: Props) {
  // Canonical intents from registry
  const { data: intentGroups, isLoading: intentsLoading } = useIntents();
  // Selected intents for this place
  const { data: selectedIntentKeys = [] } = usePlaceIntents(placeId);
  const intentMutation = usePlaceIntentsMutation();




  const update = (partial: Partial<PlaceCoreData>) => onChange({ ...data, ...partial });

  const selectedCount = selectedIntentKeys.length;
  const atMax = selectedCount >= MAX_PLACE_INTENTS;

  const toggleIntent = (intentId: string, intentKey: string) => {
    if (!placeId) return;
    const isSelected = selectedIntentKeys.includes(intentKey);
    intentMutation.mutate({
      placeId,
      intentId,
      intentKey,
      enable: !isSelected,
      currentCount: selectedCount,
    });
  };

  const MAX_SEASONS = 3;

  const toggleSeason = (value: string) => {
    const current = data.seasons || [];
    if (current.includes(value)) {
      update({ seasons: current.filter(v => v !== value) });
    } else {
      if (current.length >= MAX_SEASONS) return;
      update({ seasons: [...current, value] });
    }
  };

  const vibeLen = (data.vibe || "").length;
  const dykLen = (data.did_you_know || "").length;
  const seasonCount = (data.seasons || []).length;

  return (
    <div className="space-y-8">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-primary">Platsens upplevelse</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Redaktionella fält som gör platsen levande. Dessa visas i den nya PlaceCore-vyn.
          </p>
        </div>
      </div>

      {/* Vibe text with AI assistant */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="pc-vibe">Så upplevs platsen</Label>
          {placeName && (
            <EditorialAiAssistant
              task="place_experience_suggest"
              buildContext={() => ({
                name: placeName,
                municipality,
                landskap,
                tags: tags?.slice(0, 6),
                seasons: data.seasons,
                intentLabels: selectedIntentKeys,
                currentText: data.vibe || undefined,
              })}
              onAccept={(text) => update({ vibe: text })}
              disabled={disabled}
              allowRewrite
              hasExistingText={(data.vibe || "").trim().length > 0}
              showAdvanced
              lengthProfile="body"
              currentText={data.vibe || undefined}
            />
          )}
        </div>
        <FarmlyRichTextField
          id="pc-vibe"
          label=""
          value={data.vibe || ""}
          onChange={(v) => update({ vibe: v })}
          placeholder="3–5 meningar om platsens känsla, tempo och stämning. Observerande, aldrig säljig."
          maxLength={MAX_VIBE_LENGTH}
          disabled={disabled}
          warnBelow={80}
          warnMessage="Kort – sikta på minst 80 tecken."
        />
      </div>

      {/* ═══ Platsens karaktär — unified intents (max 6) ═══ */}
      <div className="space-y-3">
        <Label>Platsens karaktär</Label>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          Välj upp till {MAX_PLACE_INTENTS}. Används för rekommendationer och visas i appen.
        </p>

        {intentsLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Laddar alternativ…
          </div>
        ) : (
          <div className="space-y-4">
            {(intentGroups ?? []).map(group => (
              <div key={group.key}>
                <p className="text-xs text-muted-foreground/70 font-medium mb-1.5">
                  {group.icon} {group.label}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {group.intents.map(intent => {
                    const selected = selectedIntentKeys.includes(intent.key);
                    const isDisabled = disabled || (atMax && !selected) || intentMutation.isPending;
                    return (
                      <Badge
                        key={intent.id}
                        role="button"
                        tabIndex={0}
                        aria-disabled={isDisabled || undefined}
                        variant={selected ? "default" : "outline"}
                        className={`select-none transition-colors ${
                          selected
                            ? "bg-primary text-primary-foreground cursor-pointer"
                            : isDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer hover:bg-muted"
                        }`}
                        onClick={() => !isDisabled && toggleIntent(intent.id, intent.key)}
                      >
                        {intent.label_sv}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {selectedCount} / {MAX_PLACE_INTENTS} valda
          {atMax && <span className="text-amber-600 dark:text-amber-400 ml-1">— max {MAX_PLACE_INTENTS} nådd.</span>}
        </p>
      </div>

      {/* Seasons — multi-select chips */}
      <div className="space-y-2">
        <Label>Bästa säsong</Label>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          Välj 1–3 årstider. Lämna tomt om platsen fungerar året runt.
        </p>
        <div className="flex flex-wrap gap-2">
          {SEASON_CHIP_OPTIONS.map(opt => {
            const selected = (data.seasons || []).includes(opt.value);
            const seasonAtMax = seasonCount >= MAX_SEASONS && !selected;
            return (
              <Badge
                key={opt.value}
                role="button"
                aria-disabled={seasonAtMax || undefined}
                variant={selected ? "default" : "outline"}
                className={`select-none transition-colors ${
                  selected
                    ? "bg-accent text-accent-foreground cursor-pointer"
                    : seasonAtMax
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:bg-muted"
                }`}
                title={seasonAtMax ? "Max tre säsonger" : undefined}
                onClick={() => !disabled && !seasonAtMax && toggleSeason(opt.value)}
              >
                {opt.label}
              </Badge>
            );
          })}
        </div>
        {seasonCount === 0 && (
          <p className="text-xs text-muted-foreground italic">Ingen säsong vald — tolkas som "Året runt".</p>
        )}
      </div>

      {/* Duration — optional select */}
      <div className="space-y-2">
        <Label>Vanlig tidsåtgång (valfritt)</Label>
        <Select
          value={data.duration || "none"}
          onValueChange={v => update({ duration: v === "none" ? "" : v })}
          disabled={disabled}
        >
          <SelectTrigger className="w-full md:w-[240px]">
            <SelectValue placeholder="Ingen angiven" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {DURATION_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Did you know */}
      <div className="space-y-2">
        <Label htmlFor="pc-dyk">Visste du att…</Label>
        <Textarea
          id="pc-dyk"
          value={data.did_you_know || ""}
          onChange={e => update({ did_you_know: e.target.value })}
          placeholder="1–2 meningar med minnesvärt mikrofakta om platsen."
          maxLength={200}
          className="min-h-[60px]"
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground text-right">
          <span className={dykLen > 200 ? "text-destructive" : ""}>{dykLen}/200</span>
        </p>
      </div>

      {/* Area */}
      <div className="space-y-2">
        <Label htmlFor="pc-area">Tillhör område (valfritt)</Label>
        <Input
          id="pc-area"
          value={data.area || ""}
          onChange={e => update({ area: e.target.value })}
          placeholder="T.ex. Siljansbygden, Bohuskusten"
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">Används för att gruppera närliggande platser.</p>
      </div>

      {/* Subtle text link for preview */}
      {slug && (
        <p className="text-xs text-muted-foreground pt-1">
          <a
            href={`/place/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            Förhandsgranska som besökare
          </a>
        </p>
      )}
    </div>
  );
}
